'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { X, Mail, Tag, CheckCircle, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useCartStore } from '@/stores/cart';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.autofrance.se/api';
const STORAGE_KEY = 'autofrance-exit-intent-shown';
const COOLDOWN_DAYS = 7;
const DISCOUNT_CODE = 'VALKOMMEN10';

/**
 * Non-spammy exit-intent popup. Triggers on:
 *  - Desktop: mouse leaves top of viewport
 *  - Mobile: ≥45s on site + ≥600px scroll, then 1s inactivity
 *
 * Shown at most once per COOLDOWN_DAYS. Hidden on checkout, account, and any
 * page where it would interrupt a transaction.
 */
export function ExitIntentPopup() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [redanRegistrerad, setRedanRegistrerad] = useState(false);
  const [copied, setCopied] = useState(false);
  const selectedVehicle = useCartStore((s) => s.selectedVehicle);
  const selectedPlate = useCartStore((s) => s.selectedPlate);
  const vehicleName = selectedVehicle ? `${selectedVehicle.manufacturer} ${selectedVehicle.model.replace(/\s*\([^)]*\)/, '')}`.trim() : '';

  // Routes that should never be interrupted
  const blocked =
    !pathname ||
    pathname === '/kassa' ||
    pathname.startsWith('/kassa/') ||
    pathname.startsWith('/konto') ||
    pathname.startsWith('/admin') ||
    pathname.startsWith('/foretag');

  useEffect(() => {
    if (blocked) return;
    if (typeof window === 'undefined') return;

    // Cooldown check
    try {
      const last = localStorage.getItem(STORAGE_KEY);
      if (last) {
        const lastTs = Number(last);
        if (!Number.isNaN(lastTs) && Date.now() - lastTs < COOLDOWN_DAYS * 86_400_000) return;
      }
    } catch {
      /* localStorage unavailable — show anyway */
    }

    const fire = () => {
      if (open) return;
      setOpen(true);
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
    };

    // Desktop: mouseout to top
    const onMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 0 && (e.relatedTarget == null || (e.relatedTarget as HTMLElement).nodeName === 'HTML')) {
        fire();
      }
    };

    // Mobile-ish: dwell + scroll + idle
    let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
    let startTime = Date.now();
    let scrolled = false;

    const onScroll = () => {
      if (window.scrollY > 600) scrolled = true;
    };

    const onActivity = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        const dwell = Date.now() - startTime;
        if (dwell >= 45_000 && scrolled) fire();
      }, 1500);
    };

    document.addEventListener('mouseout', onMouseOut);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchstart', onActivity, { passive: true });
    window.addEventListener('touchend', onActivity, { passive: true });
    window.addEventListener('scroll', onActivity, { passive: true });

    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchstart', onActivity);
      window.removeEventListener('touchend', onActivity);
      window.removeEventListener('scroll', onActivity);
      if (inactivityTimer) clearTimeout(inactivityTimer);
    };
  }, [blocked, open]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (blocked || !open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error('Ange en giltig e-postadress');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/track/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name.trim() || undefined, phone: phone.trim() || undefined, source: 'exit_intent' }),
      });
      if (!res.ok) throw new Error('failed');
      // Servern säger om adressen var ny eller redan hade en kod. Samma adress får
      // ALDRIG en ny kod — men vi säger det rakt ut i stället för att låtsas.
      try {
        const data = await res.clone().json();
        setRedanRegistrerad(data?.status === 'redan_utfardad');
      } catch { /* svaret gick inte att tolka — visa standardtexten */ }
      // If the visitor has looked up their car, also feed the vehicle-specific
      // remarketing pipeline (vehicle_leads) so the weekly cron can follow up with
      // "parts for your [car]". Reuses the same consented capture; fire-and-forget.
      if (selectedVehicle?.ktype) {
        fetch(`${API_URL}/regnum/lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: value,
            ktype: selectedVehicle.ktype,
            plate: selectedPlate || undefined,
            vehicle_name: vehicleName,
            manufacturer: selectedVehicle.manufacturer,
            model: selectedVehicle.model,
            consent: true,
            source: 'exit_intent',
          }),
        }).catch(() => {});
      }
      setSubmitted(true);
    } catch {
      toast.error('Något gick fel — försök igen om en stund');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(DISCOUNT_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Kunde inte kopiera koden');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-popup-title"
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Stäng"
          className="absolute top-3 right-3 h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-black/5 text-gray-500"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Decorative header */}
        <div className="bg-gradient-to-br from-[#0055A4] to-[#0f2e5a] text-white px-6 pt-8 pb-6 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-300 text-[#0f2e5a] mb-3">
            <Tag className="h-7 w-7" />
          </div>
          <h2 id="exit-popup-title" className="font-heading text-2xl font-bold leading-tight">
            Vänta — 10% rabatt på din första beställning
          </h2>
          <p className="text-sm text-white/85 mt-1">
            {vehicleName
              ? `Få din rabattkod mejlad — och besked när vi får in eller sänker priset på delar till din ${vehicleName}.`
              : 'Anmäl dig så mejlar vi din personliga rabattkod.'}
          </p>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-700 mb-3">
                <CheckCircle className="h-6 w-6" />
              </div>
              <p className="font-semibold text-gray-900 mb-2">
                {redanRegistrerad ? 'Du har redan en kod' : 'Tack! Kolla din inkorg'}
              </p>
              <p className="text-sm text-gray-600 mb-1">
                {redanRegistrerad
                  ? <>Adressen <span className="font-medium text-gray-900">{email}</span> har redan fått en personlig rabattkod. Leta efter mejlet från oss.</>
                  : <>Vi har skickat din personliga rabattkod till <span className="font-medium text-gray-900">{email}</span>.</>}
              </p>
              <p className="text-xs text-gray-500">
                Koden är personlig och gäller en gång. Hittar du inte mejlet — titta i skräpposten.
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mt-5 w-full inline-flex items-center justify-center px-4 py-3 rounded-lg bg-[#0055A4] hover:bg-[#0f2e5a] text-white font-semibold"
              >
                Fortsätt handla
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <label htmlFor="exit-email" className="block text-sm font-medium text-gray-700">
                Din e-postadress
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  id="exit-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="namn@exempel.se"
                  className="w-full h-12 pl-9 pr-3 rounded-lg border border-gray-300 focus:border-[#0055A4] focus:ring-2 focus:ring-[#0055A4]/20 outline-none text-base"
                />
              </div>
              {/* Namn och telefon är VALFRIA — inga required-attribut, och det står
                  uttryckligen i etiketten. Ett extra obligatoriskt fält i en popup
                  sänker svarsfrekvensen; frivilligt ger oss data utan att kosta anmälningar. */}
              <label htmlFor="exit-name" className="block text-sm font-medium text-gray-700 pt-1">
                Namn <span className="font-normal text-gray-400">(valfritt)</span>
              </label>
              <input
                id="exit-name"
                name="name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="För- och efternamn"
                className="w-full h-12 px-3 rounded-lg border border-gray-300 focus:border-[#0055A4] focus:ring-2 focus:ring-[#0055A4]/20 outline-none text-base"
              />
              <label htmlFor="exit-phone" className="block text-sm font-medium text-gray-700 pt-1">
                Telefon <span className="font-normal text-gray-400">(valfritt)</span>
              </label>
              <input
                id="exit-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="070-123 45 67"
                className="w-full h-12 px-3 rounded-lg border border-gray-300 focus:border-[#0055A4] focus:ring-2 focus:ring-[#0055A4]/20 outline-none text-base"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center px-4 py-3 rounded-lg bg-[#0055A4] hover:bg-[#0f2e5a] disabled:opacity-60 text-white font-semibold"
              >
                {submitting ? 'Skickar…' : 'Mejla min rabattkod'}
              </button>
              <p className="text-[11px] text-gray-500 text-center">
                Vi skickar bara enstaka erbjudanden. Du kan när som helst avregistrera dig.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
